/**
 * Localization strings used for displaying Attachments
 *
 * @module
 */

import { createDictionary } from './utils';

export const batchEditText = createDictionary({
  batchEdit: {
    'en-us': 'Batch Edit',
    'de-ch': 'Stapelbearbeitung',
    'es-es': 'Edición por lotes',
    'fr-fr': 'Modification par lots',
    'pt-br': 'Edição em lote',
    'ru-ru': 'Пакетное редактирование',
    'uk-ua': 'Пакетне редагування',
    'hr-hr': 'Skupno uređivanje',
    nb: 'Grupperedigering',
  },
  batchEditPrefs: {
    'en-us': 'Batch Edit Preferences',
    'de-ch': 'Stapelbearbeitungseinstellungen',
    'es-es': 'Preferencias de edición por lotes',
    'fr-fr': 'Préférences de modification par lots',
    'pt-br': 'Preferências de edição em lote',
    'ru-ru': 'Настройки пакетного редактирования',
    'uk-ua': 'Налаштування пакетного редагування',
    'hr-hr': 'Postavke grupnog uređivanja',
    nb: 'Innstillinger for batchredigering',
  },
  numberOfRecords: {
    'en-us': 'Number of records selected from the query',
    'de-ch': 'Anzahl der aus der Abfrage ausgewählten Datensätze',
    'es-es': 'Número de registros seleccionados de la consulta',
    'fr-fr': "Nombre d'enregistrements sélectionnés dans la requête",
    'pt-br': 'Número de registros selecionados na consulta',
    'ru-ru': 'Количество записей, выбранных из запроса.',
    'uk-ua': 'Кількість записів, вибраних із запиту',
    'hr-hr': 'Broj zapisa odabranih iz upita',
    nb: 'Antall poster valgt fra spørringen',
  },
  removeField: {
    'en-us':
      'Field not supported for batch edit. Either remove the field, or make it hidden.',
    'de-ch':
      'Dieses Feld wird für die Stapelbearbeitung nicht unterstützt. Entfernen Sie das Feld oder blenden Sie es aus.',
    'es-es':
      'Este campo no admite la edición por lotes. Elimine el campo o hágalo oculto.',
    'fr-fr':
      "Ce champ n'est pas compatible avec la modification par lots. Veuillez le supprimer ou le masquer.",
    'pt-br':
      'Este campo não é compatível com edição em lote. Remova-o ou oculte-o.',
    'ru-ru':
      'Поле не поддерживается для пакетного редактирования. Либо удалите поле, либо скройте его.',
    'uk-ua':
      'Поле не підтримується для пакетного редагування. Видаліть поле або зробіть його прихованим.',
    'hr-hr':
      'Polje nije podržano za skupno uređivanje. Uklonite polje ili ga sakrijte.',
    nb: 'Feltet støttes ikke for batchredigering. Fjern feltet, eller gjør det skjult.',
  },
  addTreeRank: {
    'en-us':
      'The following ranks will be added to the query to enable batch editing',
    'de-ch':
      'Die folgenden Ränge werden der Abfrage hinzugefügt, um die Stapelbearbeitung zu ermöglichen.',
    'es-es':
      'Se añadirán los siguientes rangos a la consulta para permitir la edición por lotes.',
    'fr-fr':
      "Les rangs suivants seront ajoutés à la requête pour permettre l'édition par lots",
    'pt-br':
      'As seguintes classificações serão adicionadas à consulta para permitir a edição em lote.',
    'ru-ru':
      'Следующие ранги будут добавлены к запросу для обеспечения возможности пакетного редактирования.',
    'uk-ua':
      'Наступні ранги будуть додані до запиту, щоб увімкнути пакетне редагування',
    'hr-hr':
      'Sljedeći rangovi bit će dodani upitu kako bi se omogućilo skupno uređivanje',
    nb: 'Følgende ranger vil bli lagt til spørringen for å muliggjøre batchredigering',
  },
  pickTreesToFilter: {
    'en-us':
      'The selected rank(s) are found in multiple trees. Pick tree(s) to batch edit with',
    'de-ch':
      'Die ausgewählten Ränge befinden sich in mehreren Bäumen. Wählen Sie die Bäume aus, die Sie stapelweise bearbeiten möchten.',
    'es-es':
      'El/los rango(s) seleccionado(s) se encuentran en varios árboles. Elija el/los árbol(es) para editar por lotes.',
    'fr-fr':
      'Le ou les rangs sélectionnés se trouvent dans plusieurs arbres. Sélectionnez le ou les arbres à modifier par lots',
    'pt-br':
      'Os rankings selecionados são encontrados em várias árvores. Selecione a(s) árvore(s) para editar em lote.',
    'ru-ru':
      'Выбранный(е) ранг(и) встречается(ются) в нескольких деревьях. Выберите дерево(я) для пакетного редактирования.',
    'uk-ua':
      'Вибрані ранги знаходяться в кількох деревах. Виберіть дерево(а) для пакетного редагування.',
    'hr-hr':
      'Odabrani rang(ovi) nalaze se u više stabala. Odaberite stablo(a) za skupno uređivanje',
    nb: 'Den/de valgte rangeringen(e) finnes i flere trær. Velg tre(r) for batchredigering med',
  },
  datasetName: {
    'en-us': '{queryName:string} {datePart:string}',
    'de-ch': '{queryName:string} {datePart:string}',
    'es-es': '{queryName:string} {datePart:string}',
    'fr-fr': '{queryName:string} {datePart:string}',
    'pt-br': '{queryName:string} {datePart:string}',
    'ru-ru': '{queryName:string} {datePart:string}',
    'uk-ua': '{queryName:string} {datePart:string}',
    'hr-hr': '{queryName:string} {datePart:string}',
    nb: '{queryName:string} {datePart:string}',
  },
  errorInQuery: {
    'en-us': 'Following errors were found in the query',
    'de-ch': 'Bei der Abfrage wurden folgende Fehler gefunden.',
    'es-es': 'Se encontraron los siguientes errores en la consulta:',
    'fr-fr': 'Les erreurs suivantes ont été détectées dans la requête',
    'pt-br': 'Os seguintes erros foram encontrados na consulta.',
    'ru-ru': 'В запросе были обнаружены следующие ошибки.',
    'uk-ua': 'У запиті виявлено такі помилки',
    'hr-hr': 'U upitu su pronađene sljedeće greške',
    nb: 'Følgende feil ble funnet i spørringen',
  },
  missingRanksInQuery: {
    'en-us': 'Query requires additional ranks for batch editing',
    'de-ch': 'Für die Stapelbearbeitung sind zusätzliche Ränge erforderlich.',
    'es-es':
      'La consulta requiere rangos adicionales para la edición por lotes.',
    'fr-fr':
      "La requête nécessite des niveaux hiérarchiques supplémentaires pour l'édition par lots",
    'pt-br': 'A consulta requer níveis adicionais para edição em lote.',
    'ru-ru':
      'Для пакетного редактирования запроса требуются дополнительные ранги.',
    'uk-ua': 'Запит потребує додаткових рангів для пакетного редагування',
    'hr-hr': 'Upit zahtijeva dodatne rangove za skupno uređivanje',
    nb: 'Spørringen krever flere ranger for batchredigering',
  },
  createUpdateDataSetInstructions: {
    'en-us': 'Use the query builder to make a new batch edit dataset',
    'de-ch':
      'Verwenden Sie den Abfrage-Generator, um ein neues Batch-Bearbeitungs-Dataset zu erstellen.',
    'es-es':
      'Utilice el generador de consultas para crear un nuevo conjunto de datos de edición por lotes.',
    'fr-fr':
      "Utilisez le générateur de requêtes pour créer un nouveau jeu de données d'édition par lots.",
    'pt-br':
      'Use o construtor de consultas para criar um novo conjunto de dados para edição em lote.',
    'ru-ru':
      'Используйте конструктор запросов для создания нового набора данных для пакетного редактирования.',
    'uk-ua':
      'Використовуйте конструктор запитів для створення нового набору даних для пакетного редагування',
    'hr-hr':
      'Pomoću alata za izradu upita napravite novi skup podataka za grupno uređivanje',
    nb: 'Bruk spørrebyggeren til å opprette et nytt datasett for batchredigering',
  },
  showRollback: {
    'en-us': 'Show rollback button',
    'de-ch': 'Schaltfläche „Zurück“ anzeigen',
    'es-es': 'Mostrar botón de reversión',
    'fr-fr': 'Afficher le bouton de restauration',
    'pt-br': 'Mostrar botão de reversão',
    'ru-ru': 'Показать кнопку отката',
    'uk-ua': 'Показати кнопку відкату',
    'hr-hr': 'Prikaži gumb za vraćanje',
    nb: 'Vis tilbakerullingsknapp',
  },
  showRollbackDescription: {
    'en-us':
      'Rollback in Batch Edit is an experimental feature. This preference will hide the button',
    'de-ch':
      'Die Funktion „Rollback“ in der Stapelbearbeitung ist experimentell. Diese Einstellung blendet die Schaltfläche aus.',
    'es-es':
      'La función de reversión en la edición por lotes es una función experimental. Esta preferencia ocultará el botón.',
    'fr-fr':
      'La fonction « Annuler » dans l’édition par lots est expérimentale. Cette préférence masquera le bouton.',
    'pt-br':
      'A reversão na edição em lote é um recurso experimental. Esta preferência ocultará o botão.',
    'ru-ru':
      'Функция отката в пакетном редактировании является экспериментальной. Эта настройка скроет кнопку.',
    'uk-ua':
      'Відкат у пакетному редагуванні – це експериментальна функція. Цей параметр приховає кнопку',
    'hr-hr':
      'Vraćanje u skupnom uređivanju je eksperimentalna značajka. Ova će postavka sakriti gumb',
    nb: 'Tilbakerulling i batchredigering er en eksperimentell funksjon. Denne innstillingen vil skjule knappen',
  },
  commit: {
    'en-us': 'Commit',
    'de-ch': 'Begehen',
    'es-es': 'Comprometerse',
    'fr-fr': 'Verser en base',
    'pt-br': 'Comprometer-se',
    'ru-ru': 'Совершить',
    'uk-ua': 'Здійснити',
    'hr-hr': 'Potvrdi',
    nb: 'Begå',
  },
  startCommitDescription: {
    'en-us':
      'Commiting the Data Set will update, add, and delete the data from the spreadsheet to the  Specify database.',
    'de-ch':
      'Durch das Übertragen des Datensatzes werden die Daten aus der Tabellenkalkulation in die Specify-Datenbank aktualisiert, hinzugefügt und gelöscht.',
    'es-es':
      'Al confirmar el conjunto de datos, se actualizarán, agregarán y eliminarán los datos de la hoja de cálculo en la base de datos especificada.',
    'fr-fr':
      "L'enregistrement du jeu de données mettra à jour, ajoutera et supprimera les données de la feuille de calcul dans la base de données Specify.",
    'pt-br':
      'Ao confirmar o conjunto de dados, os dados da planilha serão atualizados, adicionados e excluídos do banco de dados especificado.',
    'ru-ru':
      'При сохранении набора данных будут обновляться, добавляться и удаляться данные из электронной таблицы в указанную базу данных.',
    'uk-ua':
      'Запис набору даних оновить, додасть та видалить дані з електронної таблиці до бази даних Specify.',
    'hr-hr':
      'Potvrdom skupa podataka ažurirat će se, dodavati i brisati podaci iz proračunske tablice u bazu podataka Specify.',
    nb: 'Når datasettet er lagret, vil det oppdatere, legge til og slette dataene fra regnearket til Specify-databasen.',
  },
  startRevertDescription: {
    'en-us':
      'Rolling back the dataset will re-update the values, delete created records, and create new records',
    'de-ch':
      'Durch das Zurücksetzen des Datensatzes werden die Werte aktualisiert, erstellte Datensätze gelöscht und neue Datensätze erstellt.',
    'es-es':
      'Revertir el conjunto de datos actualizará los valores, eliminará los registros creados y creará nuevos registros.',
    'fr-fr':
      "La restauration des données permettra de mettre à jour les valeurs, de supprimer les enregistrements créés et d'en créer de nouveaux.",
    'pt-br':
      'Reverter o conjunto de dados atualizará os valores, excluirá os registros criados e criará novos registros.',
    'ru-ru':
      'Откат набора данных приведет к повторному обновлению значений, удалению созданных записей и созданию новых записей.',
    'uk-ua':
      'Відкат набору даних призведе до повторного оновлення значень, видалення створених записів та створення нових записів.',
    'hr-hr':
      'Vraćanje skupa podataka ponovno će ažurirati vrijednosti, izbrisati kreirane zapise i kreirati nove zapise',
    nb: 'Tilbakestilling av datasettet vil oppdatere verdiene på nytt, slette opprettede poster og opprette nye poster',
  },
  commitSuccessfulDescription: {
    'en-us':
      'Your changes have been applied to the database. See the number of records affected in each table below:',
    'de-ch':
      'Ihre Änderungen wurden in der Datenbank übernommen. Die Anzahl der betroffenen Datensätze in den einzelnen Tabellen finden Sie unten:',
    'es-es':
      'Los cambios se han aplicado a la base de datos. Consulte el número de registros afectados en cada tabla a continuación:',
    'fr-fr':
      "Vos modifications ont été appliquées à la base de données. Pour voir le nombre d'enregistrements affectés dans chaque table, cliquez sur « Résultats » dans la barre d'outils au-dessus de la grille de données.",
    'pt-br':
      'As suas alterações foram aplicadas à base de dados. Veja abaixo o número de registos afetados em cada tabela:',
    'ru-ru':
      'Ваши изменения были применены к базе данных. Ниже вы можете увидеть количество затронутых записей в каждой таблице:',
    'uk-ua':
      'Ваші зміни застосовано до бази даних. Дивіться кількість записів, на які це вплинуло, у кожній таблиці нижче:',
    'hr-hr':
      'Vaše su promjene primijenjene u bazi podataka. Pogledajte broj pogođenih zapisa u svakoj tablici u nastavku:',
    nb: 'Endringene dine er implementert i databasen. Se antall berørte poster i hver tabell nedenfor:',
  },
  dateSetRevertDescription: {
    'en-us':
      'This rolled-back Data Set is saved, however, it cannot be edited. Please re-run the query',
    'de-ch':
      'Der zurückgesetzte Datensatz wurde gespeichert, kann aber nicht bearbeitet werden. Bitte führen Sie die Abfrage erneut aus.',
    'es-es':
      'Este conjunto de datos revertido se ha guardado, pero no se puede editar. Vuelva a ejecutar la consulta.',
    'fr-fr':
      'Ce jeu de données restauré est enregistré, mais ne peut pas être modifié. Veuillez exécuter à nouveau la requête.',
    'pt-br':
      'Este conjunto de dados revertido foi salvo, porém não pode ser editado. Por favor, execute a consulta novamente.',
    'ru-ru':
      'Этот восстановленный набор данных сохранен, однако его нельзя редактировать. Пожалуйста, повторно выполните запрос.',
    'uk-ua':
      'Цей відкочений набір даних збережено, проте його не можна редагувати. Будь ласка, повторіть запит.',
    'hr-hr':
      'Ovaj vraćeni skup podataka je spremljen, međutim, ne može se uređivati. Ponovno pokrenite upit.',
    nb: 'Dette tilbakerullede datasettet er lagret, men det kan ikke redigeres. Kjør spørringen på nytt.',
  },
  committing: {
    'en-us': 'Committing',
    'de-ch': 'Verpflichtung',
    'es-es': 'Comprometerse',
    'fr-fr': 'Téléverser',
    'pt-br': 'Comprometer-se',
    'ru-ru': 'Пожертвование',
    'uk-ua': 'Здійснення',
    'hr-hr': 'Počinjanje',
    nb: 'Forpliktelse',
  },
  beStatusCommit: {
    'en-us': 'Data Set Commit Status',
    'de-ch': 'Commit-Status des Datensatzes',
    'es-es': 'Estado de confirmación del conjunto de datos',
    'fr-fr': 'État de versement du jeu de données',
    'pt-br': 'Status de confirmação do conjunto de dados',
    'ru-ru': 'Статус фиксации набора данных',
    'uk-ua': 'Стан фіксації набору даних',
    'hr-hr': 'Status potvrđivanja skupa podataka',
    nb: 'Status for iverksettelse av datasett',
  },
  startCommit: {
    'en-us': 'Begin Data Set Commit?',
    'de-ch': 'Datensatz-Commit starten?',
    'es-es': '¿Iniciar confirmación del conjunto de datos?',
    'fr-fr': 'Commencer à verser le jeu de données ?',
    'pt-br': 'Iniciar confirmação do conjunto de dados?',
    'ru-ru': 'Начало фиксации набора данных?',
    'uk-ua': 'Почати фіксацію набору даних?',
    'hr-hr': 'Započeti potvrđivanje skupa podataka?',
    nb: 'Starte datasett-iverksettelse?',
  },
  commitErrors: {
    'en-us': 'Commit Failed due to Error Cells',
    'de-ch': 'Commit fehlgeschlagen aufgrund von Fehlerzellen',
    'es-es': 'Error al confirmar la transacción debido a celdas con errores.',
    'fr-fr': 'Échec de la validation en raison de cellules en erreur',
    'pt-br': 'Falha na confirmação devido a células com erro.',
    'ru-ru': 'Фиксация не удалась из-за ошибок в ячейках.',
    'uk-ua': 'Не вдалося виконати фіксацію через комірки з помилками',
    'hr-hr': 'Potvrda nije uspjela zbog ćelija s pogreškom',
    nb: 'Utføring mislyktes på grunn av feilceller',
  },
  commitErrorsDescription: {
    'en-us': 'The Commit failed due to one or more cell value errors.',
    'de-ch':
      'Der Commit ist aufgrund eines oder mehrerer Zellwertfehler fehlgeschlagen.',
    'es-es':
      'La confirmación falló debido a uno o más errores en los valores de las celdas.',
    'fr-fr':
      "La validation a échoué en raison d'une ou plusieurs cellule(s) en erreur.",
    'pt-br':
      'A operação de confirmação (Commit) falhou devido a um ou mais erros nos valores das células.',
    'ru-ru':
      'Операция фиксации не удалась из-за одной или нескольких ошибок в значениях ячеек.',
    'uk-ua':
      'Фіксацію не вдалося виконати через одну або кілька помилок у значенні клітинки.',
    'hr-hr':
      'Potvrda nije uspjela zbog jedne ili više pogrešaka u vrijednosti ćelije.',
    nb: 'Iverksettelsen mislyktes på grunn av én eller flere celleverdifeil.',
  },
  commitCancelled: {
    'en-us': 'Commit Cancelled',
    'de-ch': 'Commit abgebrochen',
    'es-es': 'Compromiso cancelado',
    'fr-fr': 'Versement annulé',
    'pt-br': 'Compromisso cancelado',
    'ru-ru': 'Подтверждение отменено',
    'uk-ua': 'Зміна фіксації',
    'hr-hr': 'Potvrda otkazana',
    nb: 'Forpliktelse avbrutt',
  },
  commitCancelledDescription: {
    'en-us': 'Commit Cancelled Description',
    'de-ch': 'Commit abgebrochen Beschreibung',
    'es-es': 'Descripción de la cancelación del compromiso',
    'fr-fr': "Description de l'annulation du versement",
    'pt-br': 'Descrição do Compromisso Cancelado',
    'ru-ru': 'Подтверждение отменено Описание',
    'uk-ua': 'Опис скасованого підтвердження',
    'hr-hr': 'Opis otkazanog potvrđivanja',
    nb: 'Beskrivelse av forpliktelse avbrutt',
  },
  commitSuccessful: {
    'en-us': 'Commit Completed with No Errors',
    'de-ch': 'Commit erfolgreich abgeschlossen (keine Fehler)',
    'es-es': 'Confirmación completada sin errores',
    'fr-fr': 'Versement terminé sans erreur',
    'pt-br': 'Commit concluído sem erros.',
    'ru-ru': 'Фиксация изменений завершена без ошибок.',
    'uk-ua': 'Коміт завершено без помилок',
    'hr-hr': 'Potvrda završena bez grešaka',
    nb: 'Utføring fullført uten feil',
  },
  batchEditRecordSetName: {
    'en-us': 'BE commit of "{dataSet:string}"',
    'de-ch': 'BE-Commit von "{dataSet:string}"',
    'es-es': 'Confirmación BE de "{dataSet:string}"',
    'fr-fr': 'Commit BE de "{dataSet:string}"',
    'pt-br': 'Confirmação BE de "{dataSet:string}"',
    'ru-ru': 'BE commit of "{dataSet:string}"',
    'uk-ua': 'BE коміт "{dataSet:string}"',
    'hr-hr': 'BE potvrda od "{dataSet:string}"',
    nb: 'BE-commit av "{dataSet:string}"',
  },
  deferForMatch: {
    'en-us': 'Use only visible fields for match',
    'de-ch': 'Verwenden Sie für den Abgleich nur sichtbare Felder.',
    'es-es': 'Utilice solo los campos visibles para la coincidencia.',
    'fr-fr': 'Utilisez uniquement les champs visibles pour la correspondance',
    'pt-br': 'Use apenas os campos visíveis para correspondência.',
    'ru-ru': 'Для сопоставления используйте только видимые поля.',
    'uk-ua': 'Використовувати лише видимі поля для збігу',
    'hr-hr': 'Koristite samo vidljiva polja za podudaranje',
    nb: 'Bruk bare synlige felt for treff',
  },
  deferForMatchDescription: {
    'en-us':
      'If true, invisible database fields will not be used for matching. Default value is {default:boolean}',
    'de-ch':
      'Wenn diese Option aktiviert ist, werden unsichtbare Datenbankfelder nicht für den Abgleich verwendet. Der Standardwert ist {default:boolean}.',
    'es-es':
      'Si es verdadero, los campos de base de datos invisibles no se utilizarán para la coincidencia. El valor predeterminado es {default:boolean}.',
    'fr-fr':
      'Si cette option est activée, les champs invisibles de la base de données ne seront pas utilisés pour la correspondance. La valeur par défaut est {default:boolean}.',
    'pt-br':
      'Se verdadeiro, os campos invisíveis do banco de dados não serão usados para correspondência. O valor padrão é {default:boolean}.',
    'ru-ru':
      'Если значение равно true, невидимые поля базы данных не будут использоваться для сопоставления. Значение по умолчанию — {default:boolean}',
    'uk-ua':
      'Якщо значення true, невидимі поля бази даних не використовуватимуться для зіставлення. Значення за замовчуванням — {default:boolean}',
    'hr-hr':
      'Ako je vrijednost true, nevidljiva polja baze podataka neće se koristiti za podudaranje. Zadana vrijednost je {default:boolean}',
    nb: 'Hvis dette er sant, vil ikke usynlige databasefelt brukes til samsvar. Standardverdien er {default:boolean}',
  },
  deferForNullCheck: {
    'en-us': 'Use only visible fields for empty record check',
    'de-ch':
      'Verwenden Sie für die Prüfung auf leere Datensätze nur sichtbare Felder.',
    'es-es':
      'Utilice únicamente los campos visibles para la comprobación de registros vacíos.',
    'fr-fr':
      'Utiliser uniquement les champs visibles pour la vérification des enregistrements vides',
    'pt-br':
      'Usar apenas os campos visíveis para verificação de registro vazio.',
    'ru-ru': 'Для проверки на пустую запись используйте только видимые поля.',
    'uk-ua': 'Використовуйте лише видимі поля для перевірки порожніх записів',
    'hr-hr': 'Koristite samo vidljiva polja za provjeru praznih zapisa',
    nb: 'Bruk bare synlige felt for tom postsjekk',
  },
  deferForNullCheckDescription: {
    'en-us':
      'If true, invisible database fields will not be used for determining whether the record is empty or not. Default value is {default: boolean}',
    'de-ch':
      'Wenn diese Option aktiviert ist, werden unsichtbare Datenbankfelder nicht zur Bestimmung herangezogen, ob ein Datensatz leer ist oder nicht. Der Standardwert ist {default: boolean}.',
    'es-es':
      'Si es verdadero, los campos invisibles de la base de datos no se utilizarán para determinar si el registro está vacío o no. El valor predeterminado es {default: boolean}.',
    'fr-fr':
      "Si cette option est activée, les champs invisibles de la base de données ne seront pas utilisés pour déterminer si l'enregistrement est vide ou non. La valeur par défaut est {default: boolean}.",
    'pt-br':
      'Se verdadeiro, os campos invisíveis do banco de dados não serão usados para determinar se o registro está vazio ou não. O valor padrão é {default: boolean}.',
    'ru-ru':
      'Если значение равно true, невидимые поля базы данных не будут использоваться для определения того, пуста запись или нет. Значение по умолчанию — {default: boolean}.',
    'uk-ua':
      'Якщо значення true, невидимі поля бази даних не використовуватимуться для визначення того, чи є запис порожнім. Значення за замовчуванням: {default: boolean}',
    'hr-hr':
      'Ako je vrijednost true, nevidljiva polja baze podataka neće se koristiti za određivanje je li zapis prazan ili ne. Zadana vrijednost je {default: boolean}',
    nb: 'Hvis dette er sant, vil ikke usynlige databasefelt brukes til å avgjøre om posten er tom eller ikke. Standardverdien er {default: boolean}',
  },
  batchEditDisabled: {
    'en-us':
      'Batch Edit is disabled for system tables and scoping hierarchy tables',
    'de-ch':
      'Die Stapelbearbeitung ist für Systemtabellen und Bereichshierarchietabellen deaktiviert.',
    'es-es':
      'La edición por lotes está deshabilitada para las tablas del sistema y las tablas de jerarquía de ámbito.',
    'fr-fr':
      "L'édition par lots est désactivée pour les tables système et les tables de hiérarchie de portée.",
    'pt-br':
      'A edição em lote está desativada para tabelas do sistema e tabelas de hierarquia de escopo.',
    'ru-ru':
      'Пакетное редактирование отключено для системных таблиц и таблиц иерархии областей видимости.',
    'uk-ua':
      'Пакетне редагування вимкнено для системних таблиць та таблиць ієрархії області видимості',
    'hr-hr':
      'Skupno uređivanje onemogućeno je za sistemske tablice i tablice hijerarhije opsega',
    nb: 'Grupperedigering er deaktivert for systemtabeller og omfangshierarktabeller',
  },
  cannotEditAfterRollback: {
    'en-us':
      '(Batch Edit datasets cannot be edited after rollback - Read Only)',
    'de-ch':
      '(Stapelbearbeitungsdatensätze können nach einem Rollback nicht mehr bearbeitet werden – schreibgeschützt)',
    'es-es':
      '(Los conjuntos de datos de edición por lotes no se pueden editar después de la reversión; solo lectura).',
    'fr-fr':
      '(Les jeux de données modifiés par lots ne peuvent pas être modifiés après une restauration - Lecture seule)',
    'pt-br':
      '(Os conjuntos de dados editados em lote não podem ser editados após o rollback - Somente leitura)',
    'ru-ru':
      '(Наборы данных, отредактированные в пакетном режиме, нельзя редактировать после отката — только для чтения)',
    'uk-ua':
      '(Набори даних пакетного редагування не можна редагувати після відкату – лише для читання)',
    'hr-hr':
      '(Skupni skupovi podataka za skupno uređivanje ne mogu se uređivati nakon vraćanja na prethodno stanje - samo za čitanje)',
    nb: '(Datasett for batchredigering kan ikke redigeres etter tilbakestilling – skrivebeskyttet)',
  },
  enableRelationships: {
    'en-us': 'Enable relationships',
    'de-ch': 'Beziehungen ermöglichen',
    'es-es': 'Facilitar las relaciones',
    'fr-fr': 'Activer les relations',
    'pt-br': 'Promover relacionamentos',
    'ru-ru': 'Обеспечьте развитие отношений',
    'uk-ua': "Увімкнути зв'язки",
    'hr-hr': 'Omogući odnose',
    nb: 'Aktiver relasjoner',
  },
  enableRelationshipsDescription: {
    'en-us':
      'Allows batch editing relationships of the base table. Rollback is disabled when relationships are enabled',
    'de-ch':
      'Ermöglicht die Stapelbearbeitung von Beziehungen in der Basistabelle. Die Rücksetzung ist deaktiviert, wenn Beziehungen aktiviert sind.',
    'es-es':
      'Permite la edición por lotes de las relaciones de la tabla base. La reversión se desactiva cuando las relaciones están habilitadas.',
    'fr-fr':
      'Permet la modification par lots des tables liées à la table de base. La restauration est désactivée lorsque les relations sont activées.',
    'pt-br':
      'Permite a edição em lote de relacionamentos da tabela base. O rollback é desativado quando os relacionamentos estão ativados.',
    'ru-ru':
      'Позволяет пакетно редактировать связи в базовой таблице. Откат отключен, если связи включены.',
    'uk-ua':
      "Дозволяє пакетне редагування зв'язків базової таблиці. Відкат вимкнено, якщо зв'язки ввімкнено.",
    'hr-hr':
      'Omogućuje skupno uređivanje odnosa osnovne tablice. Vraćanje unatrag je onemogućeno kada su odnosi omogućeni.',
    nb: 'Tillater batchredigering av relasjoner i basistabellen. Tilbakerulling er deaktivert når relasjoner er aktivert',
  },
  commitDataSet: {
    'en-us': 'Commit Data Set',
    'de-ch': 'Datensatz übertragen',
    'es-es': 'Confirmar conjunto de datos',
    'fr-fr': 'Verser le jeu de données.',
    'pt-br': 'Conjunto de dados de confirmação',
    'ru-ru': 'Набор данных для фиксации',
    'uk-ua': 'Набір даних для фіксації',
    'hr-hr': 'Potvrdi skup podataka',
    nb: 'Bekreft datasett',
  },
  warningBatchEditText: {
    'en-us':
      'Before proceeding, please note that the following action may interrupt other users. This action may cause delays or temporary unavailability of certain features for Specify users. Please consider the impact on their experience. This action cannot be undone.',
    'de-ch':
      'Bevor Sie fortfahren, beachten Sie bitte, dass die folgende Aktion andere Nutzer beeinträchtigen kann. Dies kann zu Verzögerungen oder vorübergehender Nichtverfügbarkeit bestimmter Funktionen für bestimmte Nutzer führen. Bitte berücksichtigen Sie die Auswirkungen auf deren Nutzungserfahrung. Diese Aktion kann nicht rückgängig gemacht werden.',
    'es-es':
      'Antes de continuar, tenga en cuenta que la siguiente acción podría interrumpir a otros usuarios. Esta acción podría ocasionar retrasos o la indisponibilidad temporal de ciertas funciones para los usuarios especificados. Considere el impacto en su experiencia. Esta acción es irreversible.',
    'fr-fr':
      "Avant de poursuivre, veuillez noter que l'action suivante peut perturber d'autres utilisateurs. Cette action peut entraîner des retards ou l'indisponibilité temporaire de certaines fonctionnalités pour les utilisateurs de Specify. Veuillez tenir compte de l'impact sur leur expérience. Cette action est irréversible.",
    'pt-br':
      'Antes de prosseguir, observe que a ação a seguir pode interromper outros usuários. Essa ação pode causar atrasos ou indisponibilidade temporária de certos recursos para usuários específicos. Considere o impacto na experiência deles. Esta ação não pode ser desfeita.',
    'ru-ru':
      'Прежде чем продолжить, обратите внимание, что следующее действие может помешать другим пользователям. Это действие может вызвать задержки или временную недоступность некоторых функций для указанных пользователей. Пожалуйста, учтите влияние на их опыт использования сайта. Это действие необратимо.',
    'uk-ua':
      'Перш ніж продовжити, зверніть увагу, що наступна дія може перешкодити іншим користувачам. Ця дія може спричинити затримки або тимчасову недоступність певних функцій для визначених користувачів. Будь ласка, врахуйте вплив на їхній досвід. Цю дію не можна скасувати.',
    'hr-hr':
      'Prije nego što nastavite, imajte na umu da sljedeća radnja može poremetiti rad drugih korisnika. Ova radnja može uzrokovati kašnjenja ili privremenu nedostupnost određenih značajki za određene korisnike. Molimo vas da uzmete u obzir utjecaj na njihovo iskustvo. Ova se radnja ne može poništiti.',
    nb: 'Før du fortsetter, vær oppmerksom på at følgende handling kan forstyrre andre brukere. Denne handlingen kan forårsake forsinkelser eller midlertidig utilgjengelighet av enkelte funksjoner for Specify-brukere. Vurder hvordan dette påvirker brukeropplevelsen deres. Denne handlingen kan ikke angres.',
  },
} as const);
